/* eslint-disable max-lines */
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ConfigManager } from '~/managers/ConfigManager'
import FeatureGatingManager from '~/managers/FeatureGatingManager'
import { setFlippers as setBlockchainAddressFlippers } from '~/store/blockchainAddresses/actions'
import { setFlippers as setEarnFlippers } from '~/store/earn/actions'
import { FLIPPER_KEYS } from '~/store/flippers/const'
import { setFlippers as setLandingFlippers } from '~/store/landing/actions'
import { fecthUserIdentifier, setFlippers as setRouteFlippers } from '~/store/route/actions'
import { selectUserEmail, selectUserIdentifier } from '~/store/route/selectors'
import { selectMerchantId } from '~/store/userInfo/selectors'
import { checkFlipperIsEnabled } from './actions'
import { FlipperKey } from './types'

type FlipperRequest = {
  featureKey: string
  flipperId?: string
  defaultValue?: boolean
}

type Prop = {
  email?: undefined | string
  merchantId?: undefined | number
}

export const useAppFlippers = ({ email }: Prop) => {
  useFlippers([{ featureKey: FLIPPER_KEYS.WHITELISTING_IMPROVEMENT }], null)
  useFlippers(
    [
      { featureKey: FLIPPER_KEYS.ASB_FE_TO_PASS_TOTAL_AMOUNT_FOR_STABLECOIN_WITHDRAWAL },
      { featureKey: FLIPPER_KEYS.USDC_SWAP },
      { featureKey: FLIPPER_KEYS.APP_DASHBOARD_SWAP },
      { featureKey: FLIPPER_KEYS.USD_TRANSFER_IN },
      { featureKey: FLIPPER_KEYS.USD_TRANSFER_OUT },
      { featureKey: FLIPPER_KEYS.USD_ADD_BANK_ACCOUNT },
      { featureKey: FLIPPER_KEYS.SG_MINT_DASHBOARD },
      { featureKey: FLIPPER_KEYS.SG_TRANSACTION_TIMEZONE_CONTROL },
      { featureKey: FLIPPER_KEYS.SG_RECIPIENT_BANK_ACCOUNT_CHANGED_REMINDER },
      { featureKey: FLIPPER_KEYS.SG_OTC_FORM_APP_DASHBOARD },
      { featureKey: FLIPPER_KEYS.SG_OTC_TRX_HISTORY_APP_DASHBOARD },
      { featureKey: FLIPPER_KEYS.USDC_ERC20_DEPOSIT },
      { featureKey: FLIPPER_KEYS.USDC_ERC20_WITHDRAWAL },
      { featureKey: FLIPPER_KEYS.USDC_MATIC_DEPOSIT },
      { featureKey: FLIPPER_KEYS.USDC_MATIC_WITHDRAWAL },
      { featureKey: FLIPPER_KEYS.USDT_ERC20_DEPOSIT },
      { featureKey: FLIPPER_KEYS.USDT_ERC20_WITHDRAWAL },
      { featureKey: FLIPPER_KEYS.USDT_BEP20_DEPOSIT },
      { featureKey: FLIPPER_KEYS.USDT_BEP20_WITHDRAWAL },
      { featureKey: FLIPPER_KEYS.USDT_BEP20_UI },
      { featureKey: FLIPPER_KEYS.XUSD_ERC20_DEPOSIT },
      { featureKey: FLIPPER_KEYS.XUSD_ERC20_WITHDRAWAL },
      { featureKey: FLIPPER_KEYS.APP_DASHBOARD_USDT },
      { featureKey: FLIPPER_KEYS.XUSD_DASHBOARD_ENABLE },
      { featureKey: FLIPPER_KEYS.MANUAL_ADD_ADDRESS },
      { featureKey: FLIPPER_KEYS.USDT_USDC_SWAP_APP_DASHBOARD },
      { featureKey: FLIPPER_KEYS.USDT_XUSD_SWAP_APP_DASHBOARD },
      { featureKey: FLIPPER_KEYS.RENEW_DEPOSIT_ADDRESS },
      { featureKey: FLIPPER_KEYS.BITGO_DEPOSIT_ADDRESS_ROTATION_STAGING },
      { featureKey: FLIPPER_KEYS.BITGO_DEPOSIT_ADDRESS_ROTATION_PRODUCTION },
      { featureKey: FLIPPER_KEYS.JUMIO_FLOW_FOR_SG_USER },
      { featureKey: FLIPPER_KEYS.HTS_DIRECT_MINT_DISABLED },
      { featureKey: FLIPPER_KEYS.ZRC_DIRECT_MINT_DISABLED },
      { featureKey: FLIPPER_KEYS.KILLSWITCH },
      { featureKey: FLIPPER_KEYS.COOLING_DOWN },
      { featureKey: FLIPPER_KEYS.SWAP_DISABLED_ACCURATE_FEE_CALCULATION },
      { featureKey: FLIPPER_KEYS.DPT2_TNC },
      { featureKey: FLIPPER_KEYS.TRON_WALLETCONNECT_DROPDOWN_UI },
      { featureKey: FLIPPER_KEYS.ZENDESK_ANSWERBOT },
      { featureKey: FLIPPER_KEYS.FRESHDESK_ANSWERBOT },
      { featureKey: FLIPPER_KEYS.SHIELD },
      { featureKey: FLIPPER_KEYS.METAMASK_ADD_BLOCKCHAIN_ADDRESS },
      { featureKey: FLIPPER_KEYS.SWIFT_MEPS_NETWORK },
      { featureKey: FLIPPER_KEYS.SWAP_USDT_USDC },
      { featureKey: FLIPPER_KEYS.SWAP_USDT_XUSD },
      { featureKey: FLIPPER_KEYS.SWAP_XSGD_USDC },
      { featureKey: FLIPPER_KEYS.SWAP_XSGD_XUSD },
      { featureKey: FLIPPER_KEYS.SWAP_XUSD_USDC },
      { featureKey: FLIPPER_KEYS.SWAP_XSGD_USDT },
      { featureKey: FLIPPER_KEYS.SWAP_USDC_USDT },
      { featureKey: FLIPPER_KEYS.SWAP_XUSD_USDT },
      { featureKey: FLIPPER_KEYS.SWAP_SGD_XSGD },
      { featureKey: FLIPPER_KEYS.SWAP_SGD_XUSD },
      { featureKey: FLIPPER_KEYS.SWAP_SGD_USDC },
      { featureKey: FLIPPER_KEYS.SWAP_SGD_USDT },
      { featureKey: FLIPPER_KEYS.SWAP_SGD_USD },
      { featureKey: FLIPPER_KEYS.SWAP_USD_XSGD },
      { featureKey: FLIPPER_KEYS.SWAP_USD_XUSD },
      { featureKey: FLIPPER_KEYS.SWAP_USD_USDC },
      { featureKey: FLIPPER_KEYS.SWAP_USD_USDT },
      { featureKey: FLIPPER_KEYS.TRANSFER_IN_UI_REVAMP },
      { featureKey: FLIPPER_KEYS.ASSETS_UNIFICATION_PHASE_1_STAGE_1 }, // TODO(benedictkhoo): remove after assets unification is done
      { featureKey: FLIPPER_KEYS.ASSETS_UNIFICATION_PHASE_1_STAGE_2 }, // TODO(benedictkhoo): remove after assets unification is done
      { featureKey: FLIPPER_KEYS.ASSETS_UNIFICATION_PHASE_1_STAGE_3 }, // TODO(duy): remove after assets unification is done
      { featureKey: FLIPPER_KEYS.ASSETS_UNIFICATION_PHASE_1_PERSONAL_SWAP }, // TODO(duy): remove after assets unification is done
      { featureKey: FLIPPER_KEYS.DBS_CHINESE_RESTRICTION_DEPOSIT },
      { featureKey: FLIPPER_KEYS.DBS_CHINESE_RESTRICTION_WITHDRAWAL },
      { featureKey: FLIPPER_KEYS.TRON_WITHDRAWAL_ESTIMATED_NETWORK_FEE },
      { featureKey: FLIPPER_KEYS.STRAITSX_TYPEFORM_SURVEY },
      { featureKey: FLIPPER_KEYS.SG_OTC_MODAL_REDESIGN },
    ],
    email || ''
  )
}

export const useBizFlippers = ({ email }: Prop) => {
  useFlippers(
    [
      { featureKey: FLIPPER_KEYS.ASB_FE_TO_PASS_TOTAL_AMOUNT_FOR_STABLECOIN_WITHDRAWAL },
      { featureKey: FLIPPER_KEYS.WHITELISTING_IMPROVEMENT },
      { featureKey: FLIPPER_KEYS.USDC_SWAP },
      { featureKey: FLIPPER_KEYS.BIZ_DASHBOARD_SWAP },
      { featureKey: FLIPPER_KEYS.SWAP_DISABLED_ACCURATE_FEE_CALCULATION },
      { featureKey: FLIPPER_KEYS.USD_TRANSFER_IN },
      { featureKey: FLIPPER_KEYS.USD_TRANSFER_OUT },
      { featureKey: FLIPPER_KEYS.USD_ADD_BANK_ACCOUNT },
      { featureKey: FLIPPER_KEYS.SG_BIZ_MANUAL_ENHANCEMENT },
      { featureKey: FLIPPER_KEYS.SG_TRANSACTION_TIMEZONE_CONTROL },
      { featureKey: FLIPPER_KEYS.SG_RECIPIENT_BANK_ACCOUNT_CHANGED_REMINDER },
      { featureKey: FLIPPER_KEYS.SG_OTC_FORM_BIZ_DASHBOARD },
      { featureKey: FLIPPER_KEYS.SG_OTC_TRX_HISTORY_BIZ_DASHBOARD },
      { featureKey: FLIPPER_KEYS.SGD_MEPS_DEPOSIT },
      { featureKey: FLIPPER_KEYS.SGD_MEPS_WITHDRAW },
      { featureKey: FLIPPER_KEYS.SGD_SWIFT_DEPOSIT },
      { featureKey: FLIPPER_KEYS.SGD_SWIFT_WITHDRAW },
      { featureKey: FLIPPER_KEYS.ASB_SANDBOX_UI },
      { featureKey: FLIPPER_KEYS.USDC_ERC20_DEPOSIT },
      { featureKey: FLIPPER_KEYS.USDC_ERC20_WITHDRAWAL },
      { featureKey: FLIPPER_KEYS.USDC_MATIC_DEPOSIT },
      { featureKey: FLIPPER_KEYS.USDC_MATIC_WITHDRAWAL },
      { featureKey: FLIPPER_KEYS.USDT_ERC20_DEPOSIT },
      { featureKey: FLIPPER_KEYS.USDT_ERC20_WITHDRAWAL },
      { featureKey: FLIPPER_KEYS.USDT_BEP20_DEPOSIT },
      { featureKey: FLIPPER_KEYS.USDT_BEP20_WITHDRAWAL },
      { featureKey: FLIPPER_KEYS.XUSD_ERC20_DEPOSIT },
      { featureKey: FLIPPER_KEYS.XUSD_ERC20_WITHDRAWAL },
      { featureKey: FLIPPER_KEYS.USDT_BEP20_UI },
      { featureKey: FLIPPER_KEYS.BIZ_DASHBOARD_USDT },
      { featureKey: FLIPPER_KEYS.MANUAL_ADD_ADDRESS },
      { featureKey: FLIPPER_KEYS.USDT_USDC_SWAP_BIZ_DASHBOARD },
      { featureKey: FLIPPER_KEYS.USDT_XUSD_SWAP_BIZ_DASHBOARD },
      { featureKey: FLIPPER_KEYS.XUSD_DASHBOARD_ENABLE },
      { featureKey: FLIPPER_KEYS.RENEW_DEPOSIT_ADDRESS },
      { featureKey: FLIPPER_KEYS.BITGO_DEPOSIT_ADDRESS_ROTATION_STAGING },
      { featureKey: FLIPPER_KEYS.BITGO_DEPOSIT_ADDRESS_ROTATION_PRODUCTION },
      { featureKey: FLIPPER_KEYS.KILLSWITCH },
      { featureKey: FLIPPER_KEYS.COOLING_DOWN },
      { featureKey: FLIPPER_KEYS.DPT2_TNC },
      { featureKey: FLIPPER_KEYS.TRON_WALLETCONNECT_DROPDOWN_UI },
      { featureKey: FLIPPER_KEYS.STRAITSX_SANDBOX_DISABLE_MANAGE_ACCOUNTS_PAGE },
      { featureKey: FLIPPER_KEYS.ZENDESK_ANSWERBOT },
      { featureKey: FLIPPER_KEYS.FRESHDESK_ANSWERBOT },
      { featureKey: FLIPPER_KEYS.SHIELD },
      { featureKey: FLIPPER_KEYS.METAMASK_ADD_BLOCKCHAIN_ADDRESS },
      { featureKey: FLIPPER_KEYS.SWIFT_MEPS_NETWORK },
      { featureKey: FLIPPER_KEYS.SWAP_USDT_USDC },
      { featureKey: FLIPPER_KEYS.SWAP_USDT_XUSD },
      { featureKey: FLIPPER_KEYS.SWAP_XSGD_USDC },
      { featureKey: FLIPPER_KEYS.SWAP_XSGD_XUSD },
      { featureKey: FLIPPER_KEYS.SWAP_XUSD_USDC },
      { featureKey: FLIPPER_KEYS.SWAP_XSGD_USDT },
      { featureKey: FLIPPER_KEYS.SWAP_USDC_USDT },
      { featureKey: FLIPPER_KEYS.SWAP_XUSD_USDT },
      { featureKey: FLIPPER_KEYS.SWAP_SGD_XSGD },
      { featureKey: FLIPPER_KEYS.SWAP_SGD_XUSD },
      { featureKey: FLIPPER_KEYS.SWAP_SGD_USDC },
      { featureKey: FLIPPER_KEYS.SWAP_SGD_USDT },
      { featureKey: FLIPPER_KEYS.SWAP_SGD_USD },
      { featureKey: FLIPPER_KEYS.SWAP_USD_XSGD },
      { featureKey: FLIPPER_KEYS.SWAP_USD_XUSD },
      { featureKey: FLIPPER_KEYS.SWAP_USD_USDC },
      { featureKey: FLIPPER_KEYS.SWAP_USD_USDT },
      { featureKey: FLIPPER_KEYS.TRANSFER_IN_UI_REVAMP },
      { featureKey: FLIPPER_KEYS.ASSETS_UNIFICATION_PHASE_1_STAGE_1 }, // TODO(benedictkhoo): remove after assets unification is done
      { featureKey: FLIPPER_KEYS.ASSETS_UNIFICATION_PHASE_1_STAGE_2 }, // TODO(benedictkhoo): remove after assets unification is done
      { featureKey: FLIPPER_KEYS.ASSETS_UNIFICATION_PHASE_1_STAGE_3 }, // TODO(duy): remove after assets unification is done
      { featureKey: FLIPPER_KEYS.ASSETS_UNIFICATION_PHASE_1_BUSINESS_SWAP }, // TODO(duy): remove after assets unification is done
      { featureKey: FLIPPER_KEYS.TRON_WITHDRAWAL_ESTIMATED_NETWORK_FEE },
      { featureKey: FLIPPER_KEYS.SG_OTC_MODAL_REDESIGN },
    ],
    email || ''
  )
}

export const useBizFlippersByBizId = (bizId?: string) => {
  const featureKeys = [
    { featureKey: FLIPPER_KEYS.ASSETS_UNIFICATION_PHASE_1_BLACKLIST }, // TODO(benedictkhoo): remove after assets unification is done
  ]

  useFlippers(featureKeys, bizId || '')
}

export const useXidrAppFlippers = ({ email }: Prop) => {
  useFlippers([{ featureKey: FLIPPER_KEYS.RENEW_DEPOSIT_ADDRESS }], email || '')
}

export const useXidrBizFlippers = ({ email }: Prop) => {
  useFlippers([{ featureKey: FLIPPER_KEYS.RENEW_DEPOSIT_ADDRESS }], email || '')
}

export const useUnificationPhase2Flipper = () => {
  const dispatch = useDispatch()
  const id = useSelector(selectUserIdentifier)

  useEffect(() => {
    if (id) {
      dispatch(checkFlipperIsEnabled([FLIPPER_KEYS.ASSETS_UNIFICATION_PHASE_2], id))
      dispatch(checkFlipperIsEnabled([FLIPPER_KEYS.ASSETS_UNIFICATION_PHASE_2_BLACKLIST], id))
    } else {
      dispatch(fecthUserIdentifier())
    }
  }, [id])
}

export const useRootFlippers = () => {
  const isAppSite = ConfigManager.isAppSite()
  const dispatch = useDispatch()
  const flipperId = useSelector(isAppSite ? selectUserEmail : selectMerchantId)
  const commonFeatures = [
    FLIPPER_KEYS.XSGD_HTS_WITHDRAWAL_FEE_CHARGING,
    FLIPPER_KEYS.XSGD_DASHBOARD_ETHEREUM_UI,
    FLIPPER_KEYS.XSGD_DASHBOARD_HEDERA_UI,
    FLIPPER_KEYS.XSGD_DASHBOARD_POLYGON_UI,
    FLIPPER_KEYS.XSGD_DASHBOARD_AVALANCHE_UI,
    FLIPPER_KEYS.USDT_DASHBOARD_TRON_UI,
    FLIPPER_KEYS.XUSD_DASHBOARD_BNBSMART_APP_UI,
    FLIPPER_KEYS.XSGD_DASHBOARD_ARBITRUM_UI,
    FLIPPER_KEYS.XSGD_DASHBOARD_RIPPLE_UI,
    FLIPPER_KEYS.XSGD_DASHBOARD_ZILLIQA_UI,
    FLIPPER_KEYS.WHITELISTING_IMPROVEMENT,
    FLIPPER_KEYS.SSO_TWO_FA_AUTHENTICATOR,
    FLIPPER_KEYS.STRAITSX_DPT_RISK_MODAL,
    FLIPPER_KEYS.STRAITSX_ENABLE_FAZZ_VA_FOR_DASHBOARD,
    FLIPPER_KEYS.STRAITSX_TRAVEL_RULE_DEPOSIT_DECLARATION,
    FLIPPER_KEYS.STRAITSX_TRAVEL_RULE_ADDR_WHITELISTING,
  ]
  let features: FlipperKey[] = []

  if (isAppSite) {
    features = [
      FLIPPER_KEYS.STRAITSX_DPT_CHANGEOVER,
      FLIPPER_KEYS.STRAITSX_ENABLE_FAZZ_VA_FOR_DIRECT_MINT,
      FLIPPER_KEYS.STRAITSX_APP_DASHBOARD_USE_EARN,
      ...commonFeatures,
    ]
  } else {
    features = [
      FLIPPER_KEYS.STRAITSX_XSGD_ACCOUNT_STATEMENT_UI,
      FLIPPER_KEYS.STRAITSX_DASHBOARD_APPROVAL_FLOW_UI,
      ...commonFeatures,
    ]
  }

  useEffect(() => {
    if (flipperId) {
      if (!isAppSite) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        FeatureGatingManager.isEnabled(
          [FLIPPER_KEYS.XSGD_USE_FIAT_CURRENCY_NAME],
          `Merchant_${flipperId}`
        ).then(resp => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          dispatch(setRouteFlippers([FLIPPER_KEYS.XSGD_USE_FIAT_CURRENCY_NAME], resp))
        })
      }

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      FeatureGatingManager.isEnabled(features, flipperId).then(resp => {
        dispatch(setBlockchainAddressFlippers(features, resp))
        dispatch(setLandingFlippers(features, resp))

        if (isAppSite) {
          dispatch(setEarnFlippers(features, resp))
        }
      })
    }
  }, [flipperId])
}

export const useLandingFlippers = () => {
  const isAppSite = ConfigManager.isAppSite()
  const dispatch = useDispatch()
  const flipperId = useSelector(isAppSite ? selectUserEmail : selectMerchantId)
  const commonFeatures = [
    FLIPPER_KEYS.USDC_MATIC_DEPOSIT,
    FLIPPER_KEYS.USDC_MATIC_WITHDRAWAL,
    FLIPPER_KEYS.USDT_BEP20_DEPOSIT,
    FLIPPER_KEYS.USDT_BEP20_UI,
    FLIPPER_KEYS.USDT_BEP20_WITHDRAWAL,
    FLIPPER_KEYS.USDT_TRON_DEPOSIT,
    FLIPPER_KEYS.USDT_TRON_WITHDRAWAL,
    FLIPPER_KEYS.XSGD_ARB_DEPOSIT,
    FLIPPER_KEYS.XSGD_ARB_WITHDRAWAL,
    FLIPPER_KEYS.XSGD_AVAX_DEPOSIT,
    FLIPPER_KEYS.XSGD_AVAX_WITHDRAWAL,
    FLIPPER_KEYS.XSGD_ERC20_DEPOSIT,
    FLIPPER_KEYS.XSGD_ERC20_WITHDRAWAL,
    FLIPPER_KEYS.XSGD_HTS_DEPOSIT,
    FLIPPER_KEYS.XSGD_HTS_WITHDRAWAL,
    FLIPPER_KEYS.XSGD_MATIC_DEPOSIT,
    FLIPPER_KEYS.XSGD_MATIC_WITHDRAWAL,
    FLIPPER_KEYS.XSGD_XRP_DEPOSIT,
    FLIPPER_KEYS.XSGD_XRP_WITHDRAWAL,
    FLIPPER_KEYS.XSGD_ZRC2_DEPOSIT,
    FLIPPER_KEYS.XSGD_ZRC2_WITHDRAWAL,
    FLIPPER_KEYS.XUSD_ERC20_WITHDRAWAL,
  ]
  let features: FlipperKey[] = []

  if (isAppSite) {
    features = [
      FLIPPER_KEYS.XUSD_BEP20_DEPOSIT_APP,
      FLIPPER_KEYS.XUSD_BEP20_WITHDRAWAL_APP,
      ...commonFeatures,
    ]
  } else {
    features = [
      FLIPPER_KEYS.XUSD_BEP20_DEPOSIT_BIZ,
      FLIPPER_KEYS.XUSD_BEP20_WITHDRAWAL_BIZ,
      ...commonFeatures,
    ]
  }

  useEffect(() => {
    if (flipperId) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      FeatureGatingManager.isEnabled(FLIPPER_KEYS.STRAITSX_DASHBOARD_BANNER_MANAGEMENT_SYSTEM).then(
        resp => {
          // eslint-disable-next-line prettier/prettier
          dispatch(
            setLandingFlippers([FLIPPER_KEYS.STRAITSX_DASHBOARD_BANNER_MANAGEMENT_SYSTEM], resp)
          )
        }
      )

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      FeatureGatingManager.isEnabled(features, flipperId).then(resp => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        dispatch(setLandingFlippers(features, resp))
      })
    }
  }, [flipperId])
}

/**
 * useFlippers is to check flippers that needed in page components,
 * the API check only happens at the beginning
 *
 * @param requestFlippers Set of flippers need to be checked
 */
export const useFlippers = (requestFlippers: FlipperRequest[], sharedFlipperId: null | string) => {
  const dispatch = useDispatch()

  useEffect(() => {
    const featureKeys: string[] = requestFlippers.map(k => k.featureKey)

    if (sharedFlipperId && sharedFlipperId.length > 0) {
      // flipperId is specified and fetched
      dispatch(checkFlipperIsEnabled(featureKeys, sharedFlipperId))
    } else if (sharedFlipperId === null) {
      // no flipperId
      dispatch(checkFlipperIsEnabled(featureKeys))
    }
  }, [dispatch, sharedFlipperId])
}
